import { yupResolver } from '@hookform/resolvers/yup';
import Check from '@mui/icons-material/Check';
import Close from '@mui/icons-material/Close';
import Delete from '@mui/icons-material/Delete';
import Edit from '@mui/icons-material/Edit';
import WarningAmber from '@mui/icons-material/WarningAmber';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Link from '@mui/material/Link';
import Typography from '@mui/material/Typography';
import { TicketState } from '@treadinc/horizon-api-spec';
import dayjs from 'dayjs';
import { t } from 'i18next';
import { forwardRef, useCallback, useEffect, useImperativeHandle, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import { Header } from '~components/Drawer/src/ui/Header';
import { AutocompleteFormField } from '~components/FormFields/AutocompleteFormField';
import AutocompleteMaterialsTypeahead from '~components/FormFields/CommonAutocompletes/AutocompleteMaterialsTypeahead';
import { SingleDateTimeFormField } from '~components/FormFields/SingleDateTimeFormField';
import { TextFormField } from '~components/FormFields/TextFormField';
import type { AutocompleteFieldOption } from '~components/FormFields/v2/Autocomplete/types';
import { useAllowedUnitOfMeasureOptions } from '~hooks/useAllowedUnitOfMeasureOptions';
import type { MaterialTypeahead } from '~hooks/useMaterials/models';
import { Ticket } from '~hooks/useTickets/models';
import { useStores } from '~store';
import { dateFormat } from '~utils/dateTime';
import {
  getScaleTicketEditableFields,
  getScaleTicketRows,
} from '~utils/tickets/scale-ticket-fields';
import { canEditTicket } from '~utils/tickets/ticket-utils';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';

import {
  setDefaultTicketValues,
  TICKET_NUMBER_MAX_LENGTH,
  TicketFormDTO,
  ticketFormSchema,
} from '../ApprovalsComponents/ticketFormSchema';
import { Field } from './Field';

interface TicketDetailFormImperativeApi {
  setEditMode: (isEditing: boolean) => void;
}

interface TicketDetailFormProps {
  jobId: string;
  orderId?: string | null;
  ticketCompanyId?: string | null;
  vetaCenterBound?: boolean;
  onChange: (ticket: TicketFormDTO) => Promise<Ticket>;
  onEditModeChange?: (isEditing: boolean) => void;
  onDelete?: () => void;
  ticket?: Ticket;
  readonly?: boolean;
}
export const TicketDetailForm = forwardRef<
  TicketDetailFormImperativeApi,
  TicketDetailFormProps
>((props, ref) => {
  const {
    jobId,
    orderId,
    ticketCompanyId,
    vetaCenterBound,
    onChange,
    onEditModeChange,
    onDelete,
    ticket,
    readonly,
  } = props;
  const { userStore } = useStores();
  const materialsCompanyId = ticketCompanyId || getEffectiveUserCompanyId(userStore);
  const unitOfMeasureOptions = useAllowedUnitOfMeasureOptions();

  const [isEditing, setIsEditing] = useState(!ticket && !readonly);
  const [isSaving, setIsSaving] = useState(false);

  const formMethods = useForm<TicketFormDTO>({
    resolver: yupResolver(ticketFormSchema),
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
    defaultValues: setDefaultTicketValues(ticket, jobId),
  });
  const {
    control,
    setValue,
    formState: { errors },
    handleSubmit,
    reset,
  } = formMethods;

  useImperativeHandle(
    ref,
    () => ({
      setEditMode: (isEditing: boolean) => {
        if (!readonly) {
          setIsEditing(isEditing);
        }
      },
    }),
    [],
  );

  useEffect(() => {
    if (ticket && ticket.ocrProcessed) {
      setValue('ticketNumber', ticket?.ticketNumber || ticket?.ocrTicketNumber || '');
      setValue('quantity', ticket?.quantity || ticket?.ocrNetQuantityTons || '');

      // because of quick updates, serviceDate gets overwritten by createdAt
      // Update serviceDate with a delay to avoid flip flopping.
      setTimeout(() => {
        if (ticket.ocrServiceDate && !ticket.serviceDate) {
          setValue('serviceDate', new Date(ticket.ocrServiceDate));
        }
      }, 1000);
    }
  }, [ticket]);

  const onSubmit = useCallback(() => {
    handleSubmit((data) => {
      setIsSaving(true);

      onChange(data)
        .then((ticket) => {
          reset(setDefaultTicketValues(ticket, jobId));
        })
        .finally(() => {
          setIsSaving(false);
          setIsEditing(false);
        });
    })();
  }, [handleSubmit, reset, jobId]);

  const currentMaterialId = ticket?.material?.id;
  const currentMaterialName = ticket?.material?.name;
  const materialPickerMethods = useForm<{
    material: AutocompleteFieldOption<MaterialTypeahead> | null;
  }>({
    defaultValues: {
      material: currentMaterialId
        ? ({
            label: currentMaterialName ?? '',
            value: currentMaterialId,
          } as AutocompleteFieldOption<MaterialTypeahead>)
        : null,
    },
  });
  const pickedMaterial = materialPickerMethods.watch('material');

  useEffect(() => {
    setValue(
      'material',
      { id: pickedMaterial?.value ?? '', name: pickedMaterial?.label ?? '' },
      { shouldDirty: pickedMaterial?.value !== currentMaterialId },
    );
  }, [pickedMaterial?.value, pickedMaterial?.label]);

  useEffect(() => {
    if (isEditing) {
      return;
    }

    materialPickerMethods.reset({
      material: currentMaterialId
        ? ({
            label: currentMaterialName ?? '',
            value: currentMaterialId,
          } as AutocompleteFieldOption<MaterialTypeahead>)
        : null,
    });
  }, [isEditing, currentMaterialId, currentMaterialName]);

  useEffect(() => {
    if (!isEditing) {
      reset(setDefaultTicketValues(ticket, jobId));
    }
  }, [isEditing, ticket, jobId, reset]);

  useEffect(() => {
    onEditModeChange?.(isEditing);
  }, [onEditModeChange, isEditing]);

  const ticketDate = ticket?.serviceDate || ticket?.ocrServiceDate || ticket?.createdAt;

  return (
    <FormProvider {...formMethods}>
      <Box>
        <Header
          title={
            <Typography
              variant="body2"
              sx={{
                fontWeight: 'bold',
              }}
            >
              {t('approvals.ticket_details.ticket_details')}
            </Typography>
          }
        >
          {!isEditing &&
            ticket &&
            !readonly &&
            canEditTicket(ticket.state as TicketState) && (
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  color="warning"
                  disabled={isSaving}
                  onClick={onDelete}
                  startIcon={<Delete />}
                  variant="outlined"
                >
                  {t('approvals.ticket_details.delete')}
                </Button>
                <Button
                  color="inherit"
                  disabled={isSaving}
                  onClick={() => setIsEditing(true)}
                  startIcon={<Edit />}
                  variant="outlined"
                >
                  {t('approvals.ticket_details.edit')}
                </Button>
              </Box>
            )}

          {isEditing && (
            <Box sx={{ display: 'flex', gap: 1 }}>
              {ticket && (
                <Button
                  color="inherit"
                  disabled={isSaving}
                  onClick={() => setIsEditing(false)}
                  startIcon={<Close />}
                  variant="outlined"
                >
                  {t('actions.cancel')}
                </Button>
              )}

              <Button
                variant="contained"
                color="primary"
                loading={isSaving}
                startIcon={<Check />}
                onClick={onSubmit}
              >
                {t('actions.save')}
              </Button>
            </Box>
          )}
        </Header>

        <Field
          label={t('approvals.ticket_details.ticket_number')}
          value={ticket?.ticketNumber || ticket?.ocrTicketNumber || ''}
          icon={ticket?.ticketNumber || ticket?.ocrTicketNumber ? null : <AlertIcon />}
          editing={isEditing}
          editField={
            <TextFormField
              control={control}
              errors={errors}
              disabled={false}
              name="ticketNumber"
              isRequired={false}
              margin="none"
              inputProps={{ maxLength: TICKET_NUMBER_MAX_LENGTH }}
            />
          }
        />

        <Field
          label={t('approvals.ticket_details.material')}
          value={ticket?.material?.name || ''}
          icon={ticket?.material?.name ? null : <AlertIcon />}
          editing={isEditing}
          editField={
            <AutocompleteMaterialsTypeahead
              companyId={materialsCompanyId}
              orderId={orderId}
              control={materialPickerMethods.control}
              name="material"
            />
          }
        />

        <Field
          label={t('approvals.ticket_details.quantity')}
          value={ticket?.quantity || ticket?.ocrNetQuantityTons || ''}
          icon={ticket?.quantity || ticket?.ocrNetQuantityTons ? null : <AlertIcon />}
          editing={isEditing}
          editField={
            <TextFormField
              control={control}
              errors={errors}
              disabled={false}
              name="quantity"
              showErrorMessage
              isRequired={false}
              margin="none"
            />
          }
        />

        <Field
          label={t('approvals.ticket_details.unit')}
          value={ticket?.unitOfMeasure?.name || ''}
          icon={ticket?.unitOfMeasure?.name ? null : <AlertIcon />}
          editing={isEditing}
          editField={
            <AutocompleteFormField
              key={'ticket-material-key'}
              control={control}
              name="unitOfMeasure"
              isDenseText={false}
              errors={errors}
              list={unitOfMeasureOptions}
              isRequired={true}
              getValue={(item) => item.id}
              getLabel={(item) => item.name || ''}
            />
          }
        />

        <Field
          label={t('approvals.ticket_details.ticket_date')}
          value={
            ticket &&
            `${dateFormat(dayjs(ticketDate))} ${dateFormat(dayjs(ticketDate), 'hh:mm A')}`
          }
          editing={isEditing}
          editField={
            <SingleDateTimeFormField
              control={control}
              errors={errors}
              name={'serviceDate'}
              isRequired={true}
            />
          }
        />

        {vetaCenterBound && (
          <>
            <Typography
              component="h3"
              variant="body2"
              sx={{ fontWeight: 'bold', px: 2, py: 2 }}
            >
              {t('approvals.ticket_details.scale_ticket.title')}
            </Typography>

            {getScaleTicketRows(ticket).map(({ key, label, value }) => (
              <Field key={key} label={label} value={value} />
            ))}

            {getScaleTicketEditableFields(ticket).map(({ label, name, value }) => (
              <Field
                editField={
                  <TextFormField
                    control={control}
                    disabled={false}
                    errors={errors}
                    inputProps={{ 'aria-label': label }}
                    isRequired={false}
                    margin="none"
                    name={name}
                  />
                }
                editing={isEditing}
                key={name}
                label={label}
                value={value}
              />
            ))}
          </>
        )}

        <Field
          label={t('approvals.ticket_details.image_url')}
          value={
            <Link
              href={ticket?.imageUrl}
              target="_blank"
              rel="noreferrer"
              sx={{ '&:hover': { textDecoration: 'underline' } }}
            >
              {ticket?.imageUrl ? t('approvals.ticket_details.view_image') : ''}
            </Link>
          }
          icon={ticket?.imageUrl ? null : <AlertIcon />}
        />
      </Box>
    </FormProvider>
  );
});

TicketDetailForm.displayName = 'TicketDetailForm';

const AlertIcon = () => {
  return <WarningAmber color="primary" />;
};
